/**
 * AstorThread shared markdown component map.
 *
 * Design-system-ui stays markdown-dep-free — this module only ships the styled
 * element factories typed against React's HTML-element prop shapes. Portals
 * own the `react-markdown` dep and pass these components into it.
 */

import Box from '@mui/material/Box';
import { Children, type ComponentType, type HTMLAttributes, type ReactNode } from 'react';
import { fontFamilies, fontSizes } from '../../theme/typography';
import { Cite } from '../Cite';
import { CitedHeading, type CitedHeadingLevel } from '../CitedHeading';
import { Mark } from '../Mark';

interface ChildrenProps {
  children?: ReactNode;
}

// react-markdown's component props include framework-internal fields (node, etc.)
// we don't consume; widening to HTMLAttributes keeps us decoupled from its types.
type MdComponent<P = HTMLAttributes<HTMLElement>> = ComponentType<P & ChildrenProps>;

export function createAstorMarkdownComponents(): Record<string, MdComponent> {
  const Table: MdComponent = ({ children }) => (
    <Box
      sx={{
        // Outer wrapper handles horizontal scroll so the rounded border on the
        // table doesn't get clipped when content overflows.
        overflowX: 'auto',
        my: 1.5,
        '&::-webkit-scrollbar': { height: 6 },
        '&::-webkit-scrollbar-thumb': { bgcolor: 'action.hover', borderRadius: 3 },
      }}
    >
      <Box
        component="table"
        sx={{
          width: '100%',
          minWidth: 'max-content',
          borderCollapse: 'separate',
          borderSpacing: 0,
          fontSize: 'inherit',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1.5,
          overflow: 'hidden',
        }}
      >
        {children}
      </Box>
    </Box>
  );

  const Thead: MdComponent = ({ children }) => (
    <Box component="thead" sx={{ bgcolor: 'action.hover' }}>
      {children}
    </Box>
  );

  const Tr: MdComponent = ({ children }) => (
    <Box
      component="tr"
      sx={{
        '&:not(:last-of-type) td': { borderBottom: 1, borderColor: 'divider' },
      }}
    >
      {children}
    </Box>
  );

  const Th: MdComponent = ({ children }) => (
    <Box
      component="th"
      sx={{
        textAlign: 'left',
        px: 1.5,
        py: 1,
        fontWeight: 600,
        color: 'text.secondary',
        whiteSpace: 'nowrap',
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      {children}
    </Box>
  );

  const Td: MdComponent = ({ children }) => (
    <Box
      component="td"
      sx={{
        px: 1.5,
        py: 1,
        verticalAlign: 'top',
        color: 'text.primary',
        // Strip the inline-code chip inside table cells: chip styling makes
        // tabular emails/IDs visually outweigh the row label. Inline code in
        // prose still gets the chip; this rule only applies inside tables.
        '& code': { bgcolor: 'transparent', px: 0, mx: 0, fontSize: 'inherit', borderRadius: 0 },
      }}
    >
      {children}
    </Box>
  );

  const InlineCode: MdComponent = ({ children, ...rest }) => (
    <Box
      component="code"
      {...rest}
      sx={{
        px: 0.6,
        py: 0.1,
        mx: 0.1,
        borderRadius: 0.5,
        bgcolor: 'action.hover',
        fontFamily: fontFamilies.mono,
        // em (not rem) so inline code shrinks proportionally inside a Pre or
        // a smaller container.
        fontSize: '0.92em',
      }}
    >
      {children}
    </Box>
  );

  const Pre: MdComponent = ({ children }) => (
    <Box
      component="pre"
      sx={{
        my: 1,
        p: 1.25,
        borderRadius: 1,
        bgcolor: 'action.hover',
        overflowX: 'auto',
        fontFamily: fontFamilies.mono,
        fontSize: fontSizes.sm,
        lineHeight: 1.55,
        // Cancel InlineCode's chip styling when nested inside <pre>.
        '& code': { bgcolor: 'transparent', p: 0, m: 0, borderRadius: 0, fontSize: 'inherit' },
      }}
    >
      {children}
    </Box>
  );

  const Blockquote: MdComponent = ({ children }) => (
    <Box
      component="blockquote"
      sx={{
        my: 1,
        pl: 1.5,
        ml: 0,
        borderLeft: 3,
        borderColor: 'primary.main',
        color: 'text.secondary',
        fontStyle: 'italic',
      }}
    >
      {children}
    </Box>
  );

  const Ul: MdComponent = ({ children }) => (
    <Box component="ul" sx={{ pl: 3, my: 0.5, '& li': { mb: 0.25 } }}>
      {children}
    </Box>
  );

  const Ol: MdComponent = ({ children }) => (
    <Box component="ol" sx={{ pl: 3, my: 0.5, '& li': { mb: 0.25 } }}>
      {children}
    </Box>
  );

  const A: MdComponent<HTMLAttributes<HTMLAnchorElement>> = ({ children, ...rest }) => (
    <Box
      component="a"
      {...rest}
      sx={{
        color: 'primary.main',
        textDecoration: 'underline',
        textUnderlineOffset: 2,
        '&:hover': { color: 'primary.light' },
      }}
    >
      {children}
    </Box>
  );

  const Hr: MdComponent = () => (
    <Box component="hr" sx={{ my: 1.5, border: 0, borderTop: 1, borderColor: 'divider' }} />
  );

  // -----------------------------------------------------------------
  // Citation primitives (Issue #1749)
  //
  // Markdown sources can emit raw <mark>, <cite>, and <citedheading> tags
  // (use rehype-raw on the portal side). The renderer adapters pull the
  // structured props (tone, n, level, citations) off `data-*` attrs because
  // remark/rehype only preserves data-attributes for HTML pass-through.
  // -----------------------------------------------------------------

  const MarkMd: MdComponent<HTMLAttributes<HTMLElement> & { 'data-tone'?: string }> = ({
    children,
    'data-tone': dataTone,
  }) => {
    // Coerce the raw data-tone attr into the Mark tone union, defaulting to
    // 'default'. Unknown values fall back to 'default' so a malformed token
    // never throws at render time.
    const tone = dataTone === 'success' || dataTone === 'warning' ? dataTone : 'default';
    return <Mark tone={tone}>{children}</Mark>;
  };

  const CiteMd: MdComponent<HTMLAttributes<HTMLElement> & { 'data-n'?: string; 'data-source-id'?: string }> = ({
    children,
    'data-n': dataN,
    'data-source-id': dataSourceId,
  }) => {
    // Prefer the explicit data-n attr; fall back to parsing the rendered
    // text content (e.g. `<cite>3</cite>` → n=3).
    const parsedN = Number.parseInt(dataN ?? '', 10);
    let n: number = Number.isFinite(parsedN) ? parsedN : 0;
    if (n === 0) {
      const text = Children.toArray(children)
        .filter((c): c is string => typeof c === 'string')
        .join('')
        .trim();
      const inner = Number.parseInt(text.replace(/[^\d]/g, ''), 10);
      if (Number.isFinite(inner)) n = inner;
    }
    if (n <= 0) {
      // Defensive: render the original text untouched if no index resolves.
      return <>{children}</>;
    }
    return <Cite n={n} {...(dataSourceId ? { sourceId: dataSourceId } : {})} />;
  };

  const CitedHeadingMd: MdComponent<
    HTMLAttributes<HTMLElement> & { 'data-level'?: string; 'data-citations'?: string }
  > = ({ children, 'data-level': dataLevel, 'data-citations': dataCitations }) => {
    // Parse data-level (2-6); fall back to 5 (default in-page section head).
    const parsedLevel = Number.parseInt(dataLevel ?? '', 10);
    const level: CitedHeadingLevel = (parsedLevel >= 2 && parsedLevel <= 6 ? parsedLevel : 5) as CitedHeadingLevel;
    // Parse data-citations as comma-separated integers — `data-citations="1,2,7"`.
    const citations = (dataCitations ?? '')
      .split(',')
      .map((s) => Number.parseInt(s.trim(), 10))
      .filter((n) => Number.isFinite(n) && n > 0);
    return (
      <CitedHeading level={level} {...(citations.length > 0 ? { citations } : {})}>
        {children}
      </CitedHeading>
    );
  };

  return {
    table: Table,
    thead: Thead,
    tr: Tr,
    th: Th,
    td: Td,
    code: InlineCode,
    pre: Pre,
    blockquote: Blockquote,
    ul: Ul,
    ol: Ol,
    a: A as MdComponent,
    hr: Hr,
    // Citation primitives (Issue #1749)
    mark: MarkMd as MdComponent,
    cite: CiteMd as MdComponent,
    citedheading: CitedHeadingMd as MdComponent,
  };
}
